import { UIValue } from '../ui/types';
import { $api } from '../y/api';
import { makeDataStore } from '../y/data-store';
import { YFlags, YId } from '../y/types';

export type DTypeKind = 'builtin' | 'struct' | 'map' | 'enum';
export type DTypeModifier = 'array' | 'flags';

export interface DBuiltinType {
    id: YId;
    name: string;
    kind: 'builtin';

    go_type: string;
    go_package: string;
    go_nilable: boolean;
    ts_type: string;

    builtin_pg_type: string;
    builtin_pg_type_mod: string;
    builtin_ch_type: string;
}

export interface DStructField {
    id: YId;
    owner_type_id: YId;
    order: number;
    name: string;
    type_id: YId;
    modifier?: DTypeModifier;
    is_nullable: boolean;
}

export interface DStructType {
    id: YId;
    name: string;
    kind: 'struct';

    go_type?: string;
    go_package?: string;
    go_nilable: boolean;
    ts_type?: string;

    struct_pg_json: boolean;
    struct_pg_split: boolean;
    struct_ch_split: boolean;
    struct_fields: DStructField[];
}

export interface DMapType {
    id: YId;
    name: string;
    kind: 'map';

    map_key_type_id: YId;
    map_value_type_id: YId;
}

export interface DEnumType {
    id: YId;
    name: string;
    kind: 'enum';

    go_type?: string;
    go_package?: string;
    go_nilable: boolean;
    ts_type?: string;

    enum_pg_string: boolean;
    enum_go_is_valid: boolean;
    enum_values: string[];
}

export type DType = DBuiltinType | DStructType | DMapType | DEnumType;

export type DTableRelation = 'references' | 'owned_by';

export interface DTableColumn {
    id: YId;
    owner_table_id: YId;
    order: number;
    name: string;
    type_id: YId;
    modifier?: DTypeModifier;
    is_primary_key: boolean;
    is_nullable: boolean;
    is_unique: boolean;
    is_in_update: boolean;
    references_column_id?: YId;
    references_relation?: DTableRelation;
    references_doc_only: boolean;
    db_properties: Record<string, string>
    iface_by: boolean;
}

export type DTableBackend = 'pg' | 'ch';
export type DTableIface = 'get' | 'get_many' | 'get_all' | 'get_all_keys' | 'count_all' | 'delete' | 'insert' | 'insert_many' | 'insert_get' | 'upsert' | 'update' | 'update_many' | 'update_get' | 'replace';

export interface DTable {
    id: YId;
    backend: DTableBackend;
    name: string;
    entity_name: string;
    db_properties: Record<string, string>
    iface: YFlags<DTableIface>

    columns: DTableColumn[];
}

export const d_types = makeDataStore('d/types', 'd/type', (m: DType) => m, Object.assign);
export const d_tables = makeDataStore('d/tables', 'd/table', (m: DTable) => m, Object.assign);

export const d_type_kind_values: UIValue<DTypeKind>[] = [{ key: 'builtin', name: 'Builtin' }, { key: 'enum', name: 'Enum' }, { key: 'struct', name: 'Struct' }, { key: 'map', name: 'Map' }];
export const d_type_modifier_values: UIValue<DTypeModifier | null>[] = [{ key: null, name: 'None' }, { key: 'array', name: 'Array' }, { key: 'flags', name: 'Flags' }];

export function dTableCreateDB(id: YId) {
    return $api.POST(`d/table/${id}/create_db`);
}

export function dTypeCreatePG(id: YId) {
    return $api.POST(`d/type/${id}/create_pg`);
}

function snakeToCamelCase(s: string) {
    return s.split('_').map(s => s.slice(0, 1).toUpperCase() + s.slice(1)).join('');
}

export function dTypeGetPGName(_type_map: Record<YId, DType>, type: DType): string {
    if(type.kind == 'builtin')
        return type.builtin_pg_type + (type.builtin_pg_type_mod ? ' ' + type.builtin_pg_type_mod : '');
    if(type.kind == 'map')
        return 'jsonb';
    if(type.kind == 'struct' && type.struct_pg_json)
        return 'jsonb';
    if(type.kind == 'struct' && type.struct_pg_split)
        return '(' + type.struct_fields.map(f => _type_map[f.type_id].name).join(', ') + ')';
    if(type.kind == 'enum' && type.enum_pg_string)
        return 'text';
    return type.name;
}

export function dTypeGetGoName(type_map: Record<YId, DType>, type: DType): string {
    if(type.kind == 'builtin')
        return type.go_type;
    if((type.kind == 'struct' || type.kind == 'enum') && type.go_type)
        return type.go_type;
    if(type.kind == 'map')
        return `map[${dTypeGetGoName(type_map, type_map[type.map_key_type_id])}]${dTypeGetGoName(type_map, type_map[type.map_value_type_id])}`;
    return snakeToCamelCase(type.name);
}

export function dTypeGetDescription(type_map: Record<YId, DType>, type: DType): string {
    if(type.kind == 'enum')
        return `${type.enum_values.length} values`;
    if(type.kind == 'struct')
        return `${type.struct_fields.length} fields`;
    if(type.kind == 'map')
        return `${type_map[type.map_key_type_id].name} -> ${type_map[type.map_value_type_id].name}`;
    return '';
}

export function dTableGetGoEntityName(table: DTable): string {
    return snakeToCamelCase(table.entity_name);
}

export function dReferenceGetDescription(table_map: Record<YId, DTable>, column_map: Record<YId, DTableColumn>, table: DTable): string {
    const ref_tables = new Set<string>();
    for(const c of table.columns) {
        if(c.references_column_id) {
            const col = column_map[c.references_column_id];
            const table = table_map[col.owner_table_id];
            if(table) {
                ref_tables.add(table.name);
            }
        }
    }

    return Array.from(ref_tables).sort().join(', ');
}
